import { useCallback, useEffect } from 'react';
import { CalibrationModel } from 'api';
import { ValuationsApproach } from 'common/types/valuation';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { buildCalibrationPanelId } from 'pages/ValuationsAllocation/util';
import { DependencyType, RegistrationAPI } from '../useApproachDependencyGraph';
import { StableIdGenerator } from '../useApproachStableIds';

const useInitializeApproachDependencyGraph = ({
  approaches,
  calibrations,
  dependencyGraph,
  stableIds,
  shouldBuild = true,
}: {
  calibrations: CalibrationModel[];
  approaches: ValuationsApproach[];
  dependencyGraph: RegistrationAPI;
  stableIds: StableIdGenerator;
  shouldBuild?: boolean;
}): void => {
  const { register, clear } = dependencyGraph;
  const { getOrCreateId } = stableIds;

  const resolvePublicCompsApproach = useCallback(
    (pcRef: string): ValuationsApproach | null => {
      if (!pcRef) {
        return null;
      }

      return (
        approaches.find(
          approach =>
            approach.approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH &&
            (approach.panel_id === pcRef || approach.valuations_approach_gpc?.id?.toString() === pcRef)
        ) ?? null
      );
    },
    [approaches]
  );

  useEffect(() => {
    clear();

    if (!shouldBuild || (approaches.length === 0 && calibrations.length === 0)) {
      return;
    }

    // first pass adds everything as node
    approaches.forEach(approach => {
      const panelId = approach.panel_id ?? approach.panelId;
      if (!panelId) {
        return;
      }

      register({
        approachId: getOrCreateId(panelId),
        approachType: approach.approach_type as DependencyType,
        panelId,
      });
    });

    // second pass adds edges for backsolve approaches with market adjustment enabled
    const backsolves = approaches.filter(
      approach =>
        approach.approach_type === VALUATIONS_BACKSOLVE_APPROACH &&
        approach.valuations_approach_backsolve?.adjust_for_market &&
        approach.valuations_approach_backsolve?.gpc_approach
    );
    backsolves.forEach(approach => {
      const backsolvePanelId = approach.panel_id ?? approach.panelId;
      const { gpc_approach: publicCompsApproachRef } = approach.valuations_approach_backsolve ?? {};

      const pcApproach = resolvePublicCompsApproach(publicCompsApproachRef!.toString());
      const pcPanelId = pcApproach?.panelId;

      if (!pcPanelId) {
        return;
      }

      register({
        approachId: getOrCreateId(backsolvePanelId as string),
        approachType: approach.approach_type as DependencyType,
        panelId: backsolvePanelId as string,
        sourceOfTruthType: VALUATIONS_PUBLIC_COMPANIES_APPROACH,
        sourceOfTruthStableId: getOrCreateId(pcPanelId),
      });
    });

    calibrations.forEach(calibration => {
      const calibrationPanelId = buildCalibrationPanelId(calibration.panel_id!.toString());
      const {
        public_comps_approach_calibration: publicCompsApproachRef,
        temp_public_comps_reference: tempPublicCompsApproachRef,
      } = calibration;
      // early exit if there is not reference to a persisted PC approach and there is not a temp reference either
      if (!publicCompsApproachRef && !tempPublicCompsApproachRef) {
        return;
      }
      const refToUse = publicCompsApproachRef ?? tempPublicCompsApproachRef;
      const pcApproach = resolvePublicCompsApproach(refToUse!.toString());
      const pcPanelId = pcApproach?.panelId;

      if (!pcPanelId) {
        return;
      }

      register({
        approachId: getOrCreateId(calibrationPanelId),
        approachType: 'Calibration',
        panelId: calibrationPanelId,
        sourceOfTruthType: VALUATIONS_PUBLIC_COMPANIES_APPROACH,
        sourceOfTruthStableId: getOrCreateId(pcPanelId),
      });
    });

    return () => {
      clear();
    };
  }, [approaches, clear, getOrCreateId, register, resolvePublicCompsApproach, shouldBuild, calibrations]);
};

export default useInitializeApproachDependencyGraph;
